import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, ExternalLink, Globe, FileText, Newspaper, Book } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const LibraryPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-900 to-green-700 text-white py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Library & Publications
            </h1>
            <p className="text-2xl text-green-100 leading-relaxed">
              Access our research, collections, and published works
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            {/* Publications Section */}
            <Card className="hover:shadow-2xl transition-shadow duration-300 overflow-hidden">
              <div className="relative h-64 overflow-hidden bg-gradient-to-br from-green-600 to-green-800">
                <div className="absolute inset-0 flex items-center justify-center">
                  <BookOpen className="w-32 h-32 text-white/20" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent">
                  <h2 className="text-3xl font-bold text-white flex items-center">
                    <FileText className="w-8 h-8 mr-3" />
                    Publications
                  </h2>
                </div>
              </div>
              <CardContent className="p-8">
                <p className="text-gray-700 leading-relaxed mb-6">
                  Access our academic journals, research papers, and scholarly publications through Highlander Press.
                </p>
                <div className="space-y-4">
                  <div className="border-l-4 border-green-600 pl-4">
                    <h3 className="font-bold text-gray-900 mb-2">Open Access Journals</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                        <a 
                          href="https://journals.highlanderpress.org/index.php/Plurimed" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-700 hover:underline"
                        >
                          PluriMed Journal - Medical Pluralism in Highland Asia
                        </a>
                      </li>
                      <li className="flex items-start">
                        <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                        <a 
                          href="https://journals.highlanderpress.org/index.php/Highlander" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-700 hover:underline"
                        >
                          Highlander Journal - Highland Studies & Research
                        </a>
                      </li>
                    </ul>
                  </div>
                  <div className="border-l-4 border-green-600 pl-4">
                    <h3 className="font-bold text-gray-900 mb-2">Research Publications</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">Academic monographs and research papers</span>
                      </li>
                      <li className="flex items-start">
                        <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">Project reports and working papers</span>
                      </li>
                      <li className="flex items-start">
                        <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">Conference proceedings</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Highland Press Store */}
            <Card className="hover:shadow-2xl transition-shadow duration-300 overflow-hidden">
              <div className="relative h-64 overflow-hidden bg-gradient-to-br from-blue-600 to-indigo-800">
                <div className="absolute inset-0 flex items-center justify-center">
                  <Globe className="w-32 h-32 text-white/20" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent">
                  <h2 className="text-3xl font-bold text-white flex items-center">
                    <Book className="w-8 h-8 mr-3" />
                    Highland Press Store
                  </h2>
                </div>
              </div>
              <CardContent className="p-8">
                <p className="text-gray-700 leading-relaxed mb-6">
                  Purchase books, research publications, and special collections from our official Highland Press bookstore.
                </p>
                <div className="space-y-4 mb-6">
                  <div className="border-l-4 border-blue-600 pl-4">
                    <h3 className="font-bold text-gray-900 mb-2">Available Items</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <ArrowRight className="w-4 h-4 text-blue-600 mt-1 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">Published books and monographs</span>
                      </li>
                      <li className="flex items-start">
                        <ArrowRight className="w-4 h-4 text-blue-600 mt-1 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">Research series and collections</span>
                      </li>
                      <li className="flex items-start">
                        <ArrowRight className="w-4 h-4 text-blue-600 mt-1 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">Conference materials</span>
                      </li>
                      <li className="flex items-start">
                        <ArrowRight className="w-4 h-4 text-blue-600 mt-1 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">Special editions and limited prints</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <a 
                  href="https://highlanderpress.myshopify.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6">
                    Visit Highland Press Store
                    <ExternalLink className="ml-2 w-5 h-5" />
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>

          {/* Additional Resources */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Library Resources</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Newspaper className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Research Archives</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    Access our digital archives containing research data, field notes, and historical documents from projects across Highland Asia.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <BookOpen className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Oral History Collections</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    Explore our curated collections of oral histories, narratives, and indigenous knowledge documentation from highland communities.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <FileText className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Theses & Dissertations</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    Browse completed theses and dissertations by our PGCertR graduates and visiting scholars covering diverse research themes.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Highlander Press Information */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">About Highlander Press</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Highlander Press</strong> is the Highland Institute's academic publishing arm, dedicated to 
                  disseminating cutting-edge research on Highland Asia and related regions. We publish peer-reviewed 
                  journals, monographs, and edited volumes that advance scholarly understanding of highland societies, 
                  cultures, and environments.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Our commitment to open access ensures that research findings reach the widest possible audience, 
                  including communities, policymakers, and scholars worldwide.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Publishing with Us</h3>
                <p className="text-gray-700 mb-4">
                  Interested in publishing your research with Highlander Press?
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">Peer-reviewed publication process</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">Open access options available</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">Global distribution network</span>
                  </li>
                </ul>
                <a href="mailto:info@highlandinstitute.org">
                  <Button variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
                    Contact Publishing Team
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Explore Our Research</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Discover our latest publications and ongoing research projects
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/research">
              <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg">
                View Research Projects
              </Button>
            </Link>
            <a 
              href="https://journals.highlanderpress.org" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Browse Journals
                <ExternalLink className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LibraryPage;
